import React, { FunctionComponent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Checkbox,
  ContentSection,
  FormSectionGroup,
  Layout,
  PageWrap,
  SectionHeading,
} from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import ShoppingCart from '../../../../components/ShoppingCart/ShoppingCart';
import { getBrandName } from '../../../../config';
import { CustomerDetailsSection } from '../../../../resources/base/models/enums';
import { isEnv } from '../../../../services/common/miscUtils';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { customSelectors } from '../../../../services/redux/features/custom.slice';
import { CustomerDetailsSubmitSection } from '../CustomerDetailsSubmitSection';
import { SaveAndContinueModal } from '../saveAndContinueModal';
import { createInitialCreditApplication, formGroups } from '../utils';
import { AddressDataForm } from './AddressDataForm';
import { CompanyDataForm } from './CompanyDataForm';
import { ContactDataForm } from './ContactDataForm';
import { FinancialDataForm } from './FinancialDataForm';
import { PaymentDataForm } from './PaymentDataForm';

const getFormsToRender = () => {
  return formGroups.private.map((formName) => {
    switch (formName) {
      case CustomerDetailsSection.CONTACT_INFO:
        return <ContactDataForm key={formName} formName={formName} />;
      case CustomerDetailsSection.ADDRESS_INFO:
        return <AddressDataForm key={formName} formName={formName} />;
      case CustomerDetailsSection.FINANCIAL_INFO:
        return <FinancialDataForm key={formName} formName={formName} />;
      case CustomerDetailsSection.PAYMENT_INFO:
        return <PaymentDataForm key={formName} formName={formName} />;
      case CustomerDetailsSection.COMPANY_INFO:
        return <CompanyDataForm key={formName} formName={formName} />;
      default:
        return null;
    }
  });
};

const HasCoCustomerIndicator: FunctionComponent<{ hasCoCustomer: boolean }> = ({ hasCoCustomer }) => {
  return (
    <Layout className="u-mb">
      <Layout.Item>
        <Checkbox checked={hasCoCustomer} disabled className="u-float-right">
          {t('step2:cocontractor:label')}
        </Checkbox>
      </Layout.Item>
    </Layout>
  );
};

export const PrivateCustomerDetailsForms = () => {
  const ids = useSelector(creditApplicationSelectors.selectCreditApplicationIds);
  const hasCoCustomer = !!ids.applicantCreditScoreIdCoCustomer;
  const isHybridJourney = useSelector(customSelectors.selectIsHybridJourney);
  const { creditApplicationId } = useSelector(creditApplicationSelectors.selectCreditApplicationIds);

  useEffect(() => {
    if (!creditApplicationId) {
      createInitialCreditApplication();
    }
  }, [creditApplicationId]);

  return (
    <PageWrap>
      <ShoppingCart />
      {!isEnv('prod') && <SaveAndContinueModal isHybridJourney={isHybridJourney} />}
      <ContentSection altBackground={getBrandName().includes('vw')} className="u-pv-none">
        <SectionHeading className="u-mt" level={2}>
          {t('step4:heading:privateCustomer')}
        </SectionHeading>
        <HasCoCustomerIndicator hasCoCustomer={hasCoCustomer} />
        <FormSectionGroup>{getFormsToRender()}</FormSectionGroup>
      </ContentSection>
      <CustomerDetailsSubmitSection hasCoCustomer={hasCoCustomer} customerType="INDIVIDUAL" />
    </PageWrap>
  );
};
